import { useEffect, useState } from "react";
import { X } from "lucide-react";
import type { AdminUser } from "../../api/adminUsers";
import {
  fetchUserInfo,
  fetchUserTariffHistoryByUserId,
  getUserPurchases,
  type UserLimits,
  type UserTariffPurchase,
} from "../../api/user";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { AdminUserTariffCell } from "../AdminUserTariffCell/AdminUserTariffCell";
import {
  AdminUserLimitsBlock,
  AdminUserPurchaseHistoryList,
} from "../AdminUserLimitsBlock/AdminUserLimitsBlock";
import { hasRole } from "../../utils/authRoles";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import "./AdminUserModal.scss";

interface AdminUserModalProps {
  user: AdminUser;
  showArchived: boolean;
  actingUserId: number | null;
  onClose: () => void;
  onGrantAdmin: (userId: number) => void;
  onRevokeAdmin: (userId: number) => void;
}

const formatRegistrationDate = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <div className="admin-user-modal-row">
    <dt className="admin-user-modal-label">{label}</dt>
    <dd className="admin-user-modal-value">{value}</dd>
  </div>
);

export const AdminUserModal = ({
  user,
  showArchived,
  actingUserId,
  onClose,
  onGrantAdmin,
  onRevokeAdmin,
}: AdminUserModalProps) => {
  const displayName = user.alias?.trim() || user.email?.trim() || "Пользователь";
  const isAdmin = hasRole({ roles: user.roles }, "ADMIN");
  const isActing = actingUserId === user.id;
  const roles = user.roles?.length ? user.roles.join(", ") : "—";
  const hasArchived = showArchived && (user.archivedTariffs?.length ?? 0) > 0;

  const [userLimits, setUserLimits] = useState<UserLimits | null>(user.userLimits ?? null);
  const [purchaseHistory, setPurchaseHistory] = useState<UserTariffPurchase[]>(
    user.purchases ?? user.purchaseHistory ?? []
  );
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetailsLoading(true);
    setDetailsError(null);
    setUserLimits(user.userLimits ?? null);
    setPurchaseHistory(user.purchases ?? user.purchaseHistory ?? []);

    Promise.all([
      fetchUserInfo(user.id, true).catch(() => null),
      fetchUserTariffHistoryByUserId(user.id).catch(() => null),
    ])
      .then(([info, history]) => {
        if (cancelled) return;
        if (info?.userLimits != null) {
          setUserLimits(info.userLimits);
        }
        const embeddedHistory = getUserPurchases(info);
        if (Array.isArray(embeddedHistory)) {
          setPurchaseHistory(embeddedHistory);
        } else if (Array.isArray(history)) {
          setPurchaseHistory(history);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailsError(getApiErrorMessage(err, "Не удалось загрузить лимиты и историю"));
        }
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user.id, user.purchases, user.purchaseHistory, user.userLimits]);

  const hasUserLimits = Boolean(
    userLimits &&
      (userLimits.periodStart ||
        userLimits.periodEnd ||
        userLimits.limits ||
        userLimits.usage ||
        userLimits.entitlements)
  );

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div
          className="detail-checks-modal admin-user-modal"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="detail-checks-modal-head">
            <h3>{displayName}</h3>
            <button type="button" className="detail-tasklist-close" onClick={onClose} aria-label="Закрыть">
              <X size={18} />
            </button>
          </div>

          <div className="detail-checks-modal-body admin-user-modal-body">
            <section className="admin-user-modal-section">
              <h4 className="admin-user-modal-section-title">Пользователь</h4>
              <dl className="admin-user-modal-list">
                <InfoRow label="Имя" value={user.alias?.trim() || "—"} />
                <InfoRow label="Email" value={user.email?.trim() || "—"} />
                <InfoRow
                  label="Email подтверждён"
                  value={user.emailConfirmed === false ? "Нет" : user.emailConfirmed ? "Да" : "—"}
                />
                <InfoRow label="Регистрация" value={formatRegistrationDate(user.createdAt)} />
                <InfoRow label="Роли" value={roles} />
                <InfoRow label="Статус" value={user.locked ? "Заблокирован" : "Активен"} />
                <InfoRow
                  label="Неудачные попытки входа"
                  value={user.failedAttempts != null ? String(user.failedAttempts) : "—"}
                />
              </dl>
              <div className="admin-user-modal-badges">
                {isAdmin ? (
                  <span className="admin-user-modal-badge admin-user-modal-badge--admin">Админ</span>
                ) : null}
                {user.locked ? (
                  <span className="admin-user-modal-badge admin-user-modal-badge--locked">
                    Заблокирован
                  </span>
                ) : null}
                {user.emailConfirmed === false ? (
                  <span className="admin-user-modal-badge">Email не подтверждён</span>
                ) : null}
              </div>
            </section>

            <section className="admin-user-modal-section">
              <h4 className="admin-user-modal-section-title">Лимиты и период</h4>
              {detailsLoading ? (
                <div className="admin-user-modal-loading">
                  <CircularProgress size="md" />
                </div>
              ) : detailsError ? (
                <p className="admin-user-modal-empty">{detailsError}</p>
              ) : hasUserLimits ? (
                <AdminUserLimitsBlock limits={userLimits} />
              ) : (
                <>
                  <p className="admin-user-modal-hint">Нет данных user_limits — показаны активные тарифы</p>
                  <AdminUserTariffCell tariffs={user.activeTariffs} mode="active" />
                </>
              )}
            </section>

            <section className="admin-user-modal-section">
              <h4 className="admin-user-modal-section-title">Заявки</h4>
              <AdminUserTariffCell tariffs={user.requestedTariffs} mode="requested" />
            </section>

            <section className="admin-user-modal-section">
              <h4 className="admin-user-modal-section-title">История покупок</h4>
              {detailsLoading ? (
                <div className="admin-user-modal-loading">
                  <CircularProgress size="md" />
                </div>
              ) : (
                <AdminUserPurchaseHistoryList items={purchaseHistory} />
              )}
            </section>

            {hasArchived ? (
              <section className="admin-user-modal-section">
                <h4 className="admin-user-modal-section-title">Архивные тарифы</h4>
                <AdminUserTariffCell tariffs={user.archivedTariffs ?? []} mode="archived" />
              </section>
            ) : null}

            <section className="admin-user-modal-section">
              <h4 className="admin-user-modal-section-title">Сайты</h4>
              {user.requestedFor && user.requestedFor.length > 0 ? (
                <div className="admin-user-modal-sites">
                  {user.requestedFor.map((site, index) => (
                    <div
                      key={`${site.inn ?? "inn"}-${site.siteUrl ?? index}`}
                      className="admin-user-modal-site"
                    >
                      <InfoRow label="ИНН" value={site.inn?.trim() || "—"} />
                      <InfoRow label="Адрес сайта" value={site.siteUrl?.trim() || "—"} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="admin-user-modal-empty">Нет сайтов</p>
              )}
            </section>
          </div>

          <div className="detail-checks-modal-footer admin-user-modal-actions">
            {isAdmin ? (
              <button
                type="button"
                className="btn admin-user-modal-action admin-user-modal-action--danger"
                disabled={isActing}
                onClick={() => onRevokeAdmin(user.id)}
              >
                {isActing ? <CircularProgress size="sm" /> : "Убрать из администраторов"}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary admin-user-modal-action"
                disabled={isActing}
                onClick={() => onGrantAdmin(user.id)}
              >
                {isActing ? <CircularProgress size="sm" /> : "Выдать права администратора"}
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
